import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { Radio, RadioGroup } from '@opendate-app/setlist';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import useV3Budgets from '../hooks/useV3Budgets';
import { useDebouncedPerKeySave } from '../hooks/useDebouncedPerKeySave';
import { parseFloatFromString } from '../utils/parseFloatFromString';
import { hasPayoutCap } from '../utils/payoutCap';
import {
  dealBracketWidthLabel,
  GROSS_TICKET_SALES_BASIS,
  TICKET_COUNT_BASIS,
} from '../../../shared/dealBracketWidth';

// Deal-brackets editor for the V3 deal-terms IA. Brackets are tiered split
// levels on the V3 budget (V3DealBracket). Each row persists on its own through
// V3DealBracketsController (POST on add, debounced PATCH on edit, DELETE on
// remove); drag-reorder renumbers positions. Reads brackets from the context
// budget so reducer updates stay live.

const AND = 'AND';
const OR = 'OR';

const sortByPosition = (a, b) => (a.position || 0) - (b.position || 0);

const labelStyle = {
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--od-c-414651, #414651)',
  marginBottom: '4px',
  display: 'block',
};

const inputStyle = {
  border: '1px solid var(--od-c-d5d7da, #d5d7da)',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  fontSize: '14px',
  height: '40px',
  padding: '8px 12px',
};

const toggleGroupStyle = {
  border: '1px solid var(--od-c-d5d7da, #d5d7da)',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  display: 'inline-flex',
  flexShrink: 0,
};

const toggleBtnStyle = (isActive) => ({
  background: isActive ? 'var(--od-c-136395, #136395)' : 'var(--od-c-ffffff, white)',
  border: 'none',
  borderRight: '1px solid var(--od-c-d5d7da, #d5d7da)',
  padding: '0 12px',
  height: '36px',
  fontSize: '14px',
  fontWeight: 600,
  color: isActive ? 'var(--od-c-ffffff, #ffffff)' : 'var(--od-c-414651, #414651)',
  cursor: 'pointer',
  outline: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const toggleBtnLastStyle = (isActive) => ({
  ...toggleBtnStyle(isActive),
  borderRight: 'none',
});

const quantityMask = createNumberMask({ prefix: '', includeThousandsSeparator: false, allowDecimal: false, integerLimit: 10 });

const DealBracketRow = ({ dealBracket, basis, currency, isFirst, dragHandleProps, onEdit, onEditNow, onDelete }) => {
  const [quantity, setQuantity] = useState(dealBracket.quantity ?? '');
  const [grossAmount, setGrossAmount] = useState(dealBracket.gross_amount ?? '');
  const [dollarAmount, setDollarAmount] = useState(dealBracket.dollar_amount ?? '');
  const [upsidePercentage, setUpsidePercentage] = useState(dealBracket.upside_percentage ?? '');

  useEffect(() => {
    setQuantity(dealBracket.quantity ?? '');
    setGrossAmount(dealBracket.gross_amount ?? '');
    setDollarAmount(dealBracket.dollar_amount ?? '');
    setUpsidePercentage(dealBracket.upside_percentage ?? '');
  }, [dealBracket.id]);

  const isAnd = (dealBracket.dollar_or_percentage_operator || AND) === AND;

  return (
    <div className="d-flex align-items-end mb-2" style={{ gap: '8px' }}>
      <a
        href="#"
        {...(dragHandleProps || {})}
        onClick={(e) => e.preventDefault()}
        style={{ color: 'var(--od-c-717680, #717680)', marginBottom: '10px', cursor: 'grab' }}
        aria-label="Reorder bracket"
      >
        <i className="fas fa-grip-vertical" style={{ fontSize: '14px' }} />
      </a>

      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <label style={labelStyle}>
          {dealBracketWidthLabel({ bracket: { quantity, gross_amount: grossAmount }, basis, isFirst, currency })} at
        </label>
        {basis === GROSS_TICKET_SALES_BASIS ? (
          <CurrencyInput
            className="form-control"
            style={{ ...inputStyle, width: '100%' }}
            maskOptions={{ prefix: '', integerLimit: null }}
            placeholder="0"
            aria-label="Gross ticket sales in this bracket"
            value={grossAmount || ''}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              setGrossAmount(e.target.value);
              onEdit(dealBracket, { gross_amount: e.target.value });
            }}
          />
        ) : (
          <MaskedInput
            className="form-control"
            style={inputStyle}
            mask={quantityMask}
            placeholder="0"
            onFocus={(e) => e.target.select()}
            value={quantity || ''}
            onChange={(e) => {
              setQuantity(e.target.value);
              onEdit(dealBracket, { quantity: e.target.value });
            }}
          />
        )}
      </div>

      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <label style={labelStyle}>Guarantee</label>
        <CurrencyInput
          className="form-control"
          style={{ ...inputStyle, width: '100%' }}
          maskOptions={{ prefix: '', integerLimit: null }}
          placeholder="0"
          value={dollarAmount || ''}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            setDollarAmount(e.target.value);
            onEdit(dealBracket, { dollar_amount: e.target.value });
          }}
        />
      </div>

      <div style={{ alignSelf: 'flex-end', flexShrink: 0 }}>
        <div style={toggleGroupStyle}>
          <button
            type="button"
            style={toggleBtnStyle(isAnd)}
            onClick={() => { if (!isAnd) onEditNow(dealBracket, { dollar_or_percentage_operator: AND }); }}
            aria-pressed={isAnd}
          >
            +
          </button>
          <button
            type="button"
            style={toggleBtnLastStyle(!isAnd)}
            onClick={() => { if (isAnd) onEditNow(dealBracket, { dollar_or_percentage_operator: OR }); }}
            aria-pressed={!isAnd}
          >
            vs
          </button>
        </div>
      </div>

      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <label style={labelStyle}>Percentage of ticket sales</label>
        <CurrencyInput
          className="form-control"
          style={{ ...inputStyle, width: '100%' }}
          maskOptions={{ prefix: '', integerLimit: null }}
          placeholder="0.00"
          value={upsidePercentage || ''}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            setUpsidePercentage(e.target.value);
            onEdit(dealBracket, { upside_percentage: e.target.value });
          }}
        />
      </div>

      <a
        href="#"
        onClick={(e) => { e.preventDefault(); onDelete(dealBracket); }}
        style={{ color: 'var(--od-c-717680, #717680)', marginBottom: '10px' }}
        aria-label="Remove bracket"
      >
        <i className="far fa-trash" style={{ fontSize: '14px' }} />
      </a>
    </div>
  );
};

DealBracketRow.propTypes = {
  dealBracket: PropTypes.object.isRequired,
  basis: PropTypes.oneOf([TICKET_COUNT_BASIS, GROSS_TICKET_SALES_BASIS]).isRequired,
  currency: PropTypes.string,
  isFirst: PropTypes.bool.isRequired,
  dragHandleProps: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onEditNow: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const CapOverflowRow = ({ dealBracket, onEditNow }) => (
  <div
    className="d-flex align-items-center mb-2"
    style={{
      marginLeft: '30px',
      padding: '6px 10px',
      fontSize: '12px',
      color: 'var(--od-c-57606a, #57606a)',
      background: 'var(--od-c-f6f8fa, #f6f8fa)',
      borderLeft: '3px solid var(--od-c-54aeff, #54aeff)',
      borderRadius: '0 6px 6px 0',
    }}
  >
    <span>↳ On cap exceeded, route overflow to:</span>
    <select
      style={{ marginLeft: 'auto', border: '1px solid var(--od-c-d0d7de, #d0d7de)', borderRadius: '4px', padding: '2px 8px', background: 'var(--od-c-ffffff, white)' }}
      value={dealBracket.cap_overflow_target || ''}
      onChange={(e) => onEditNow(dealBracket, { cap_overflow_target: e.target.value === '' ? null : e.target.value })}
    >
      <option value="">None</option>
      <option value="headliner">Headliner</option>
      <option value="venue">Venue</option>
    </select>
  </div>
);

const sanitize = (attrs) => {
  const out = {};
  if ('quantity' in attrs) out.quantity = attrs.quantity ? parseInt(String(attrs.quantity).replace(/,/g, ''), 10) : '';
  if ('gross_amount' in attrs) out.gross_amount = attrs.gross_amount ? parseFloatFromString(attrs.gross_amount) : '';
  if ('dollar_amount' in attrs) out.dollar_amount = attrs.dollar_amount ? parseFloatFromString(attrs.dollar_amount) : '';
  if ('upside_percentage' in attrs) out.upside_percentage = attrs.upside_percentage ? parseFloatFromString(attrs.upside_percentage) : '';
  if ('dollar_or_percentage_operator' in attrs) out.dollar_or_percentage_operator = attrs.dollar_or_percentage_operator;
  if ('cap_overflow_target' in attrs) out.cap_overflow_target = attrs.cap_overflow_target;
  return out;
};

const V3DealBracketsEditor = () => {
  const { budget, team, csrfToken, offer, dispatch, isSavingDealBracketBasis } = useV3OfferContext();
  const { createDealBracket, updateDealBracket, deleteDealBracket, reorderDealBracket, updateBudget } = useV3Budgets();

  const autoSave = (_csrf, _team, { id, ...editedFields }) => {
    if (!id) return;
    updateDealBracket(id, sanitize(editedFields));
  };
  const debouncedAutoSave = useDebouncedPerKeySave(autoSave);

  const brackets = (budget?.v3_deal_brackets || [])
    .slice()
    .sort(sortByPosition);

  const showCapOverflow = hasPayoutCap(offer?.artist_payout_cap);
  const basis = budget?.deal_bracket_basis || TICKET_COUNT_BASIS;
  const currency = team?.base_currency_symbol;

  const onBasisChange = async (nextBasis) => {
    if (!budget?.id || isSavingDealBracketBasis || nextBasis === basis) return;
    const previousBasis = basis;
    dispatch({ type: V3_ACTIONS.SET_SAVING_DEAL_BRACKET_BASIS, payload: true });
    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: { deal_bracket_basis: nextBasis } });
    const saved = await updateBudget(budget.id, { deal_bracket_basis: nextBasis });
    if (!saved) dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: { deal_bracket_basis: previousBasis } });
    dispatch({ type: V3_ACTIONS.SET_SAVING_DEAL_BRACKET_BASIS, payload: false });
  };

  // When the payout cap is removed the cap-overflow routing UI disappears, but
  // any cap_overflow_target persisted on a bracket would linger in the DB and
  // silently route a future cap. Clear it so removing the cap is a clean reset.
  useEffect(() => {
    if (showCapOverflow) return;
    brackets.forEach((bracket) => {
      if (bracket.id && bracket.cap_overflow_target) {
        dispatch({ type: V3_ACTIONS.UPDATE_BUDGET_DEAL_BRACKET, payload: { id: bracket.id, cap_overflow_target: null } });
        updateDealBracket(bracket.id, { cap_overflow_target: null });
      }
    });
  }, [showCapOverflow, brackets, dispatch, updateDealBracket]);

  const onEdit = (bracket, attrs) => {
    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET_DEAL_BRACKET, payload: { id: bracket.id, ...attrs } });
    debouncedAutoSave(csrfToken, team, { id: bracket.id }, attrs);
  };

  // Optimistic update + immediate persist (discrete toggles/selects).
  const onEditNow = (bracket, attrs) => {
    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET_DEAL_BRACKET, payload: { id: bracket.id, ...attrs } });
    if (bracket.id) updateDealBracket(bracket.id, sanitize(attrs));
  };

  const onDelete = (bracket) => {
    if (bracket.id) deleteDealBracket(bracket.id);
  };

  const handleAdd = () => {
    if (!budget?.id) return;
    // Position is assigned by acts_as_list (appended to the end) server-side.
    createDealBracket(budget.id, { dollar_or_percentage_operator: AND });
  };

  const handleDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const next = [...brackets];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    // Optimistically reflect the new order; acts_as_list renumbers the siblings
    // atomically on the server from this single bracket's new position.
    dispatch({
      type: V3_ACTIONS.SET_BUDGET_DEAL_BRACKETS,
      payload: next.map((bracket, index) => ({ ...bracket, position: index + 1 })),
    });
    if (moved.id) reorderDealBracket(moved.id, result.destination.index + 1);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: '8px 24px' }}>
        <span aria-hidden="true" style={{ ...labelStyle, marginBottom: 0 }}>Set tiers by</span>
        <div role="radiogroup" aria-label="Set tiers by" className="d-flex flex-wrap" style={{ gap: '8px 24px' }}>
          <RadioGroup value={basis} onChange={onBasisChange} disabled={isSavingDealBracketBasis}>
            <Radio value={TICKET_COUNT_BASIS} label="Ticket count" />
            <Radio value={GROSS_TICKET_SALES_BASIS} label="Gross ticket sales" />
          </RadioGroup>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="v3-deal-brackets">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {brackets.map((bracket, index) => (
                <Draggable key={bracket.id || `bracket-${index}`} draggableId={String(bracket.id || `bracket-${index}`)} index={index}>
                  {(dragProvided) => (
                    <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                      <DealBracketRow
                        dealBracket={bracket}
                        basis={basis}
                        currency={currency}
                        isFirst={index === 0}
                        dragHandleProps={dragProvided.dragHandleProps}
                        onEdit={onEdit}
                        onEditNow={onEditNow}
                        onDelete={onDelete}
                      />
                      {showCapOverflow && (
                        <CapOverflowRow dealBracket={bracket} onEditNow={onEditNow} />
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div style={{ marginTop: brackets.length === 0 ? '0px' : '8px', marginBottom: '20px' }}>
        <a href="#" onClick={(e) => { e.preventDefault(); handleAdd(); }} className="text-primary">
          <strong><i className="fal fa-plus" /> Add bracket</strong>
        </a>
      </div>
    </div>
  );
};

export default V3DealBracketsEditor;
